import { Link } from "react-router-dom";
import { Lock, Zap, CheckCircle, Crown, Clock, Heart, Star } from "lucide-react";

export const UpgradeModal = ({ isOpen, onClose, reason, resetTime }) => {
  if (!isOpen) return null;

  const formatResetTime = (isoTime) => {
    if (!isoTime) return "midnight";
    try {
      const date = new Date(isoTime);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return "midnight";
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl max-w-md w-full p-8 animate-pop-in">
        {/* Icon */}
        <div className="w-20 h-20 bg-gradient-to-br from-pink-500 to-rose-600 rounded-full flex items-center justify-center mx-auto mb-6">
          <Heart className="w-10 h-10 text-white" />
        </div>

        {/* Title */}
        <h2 className="font-heading font-black text-2xl text-slate-800 text-center mb-2" data-testid="paywall-title">
          Don't stop now!
        </h2>

        {/* Subtitle - Exact paywall message */}
        <p className="text-slate-600 text-center mb-6 leading-relaxed" data-testid="paywall-message">
          {reason || "You've used all 10 free questions today. Your STAAR test is coming — don't stop now. Upgrade to Premium for unlimited practice."}
        </p>

        {/* Reset Time */}
        <div className="flex items-center justify-center gap-2 text-sm text-slate-400 mb-6">
          <Clock className="w-4 h-4" />
          <span>Free practice resets at {formatResetTime(resetTime)} UTC</span>
        </div>

        {/* Premium Benefits - Relief focused */}
        <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-2xl p-4 mb-6 border border-pink-200">
          <p className="font-bold text-pink-800 mb-3 flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
            Family Pack - Most Popular
          </p>
          <ul className="space-y-2">
            {[
              "Unlimited practice until they're ready",
              "Know exactly when they'll pass",
              "See what they need to work on",
              "AI tutor help anytime they're stuck",
              "Covers up to 4 kids"
            ].map((benefit, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-pink-700">
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                {benefit}
              </li>
            ))}
          </ul>
          <p className="text-xs text-pink-600 mt-3 text-center">That's just $25 per child per year</p>
        </div>

        {/* CTA Buttons */}
        <div className="space-y-3">
          <Link
            to="/membership?plan=family"
            className="block w-full py-4 bg-gradient-to-r from-pink-500 to-rose-600 text-white font-bold rounded-2xl text-center hover:from-pink-600 hover:to-rose-700 transition-all flex items-center justify-center gap-2 shadow-lg"
          >
            <Heart className="w-5 h-5" />
            Get Family Pack - $99/year
          </Link>
          <Link
            to="/pricing"
            className="block w-full py-3 text-indigo-600 font-medium text-center hover:text-indigo-700 transition-colors"
          >
            See all plans
          </Link>
          <button
            onClick={onClose}
            className="w-full py-2 text-slate-400 text-sm hover:text-slate-600 transition-colors"
          >
            Continue tomorrow
          </button>
        </div>
      </div>
    </div>
  );
};

export const DailyUsageBadge = ({ used, limit, type = "questions" }) => {
  if (limit === -1) return null; // Premium - no limit
  
  const remaining = limit - used;
  const isLow = remaining <= 1;
  const isExhausted = remaining <= 0;

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
      isExhausted 
        ? 'bg-red-100 text-red-700' 
        : isLow 
        ? 'bg-amber-100 text-amber-700'
        : 'bg-slate-100 text-slate-600'
    }`}>
      <Zap className={`w-4 h-4 ${isExhausted ? 'text-red-500' : isLow ? 'text-amber-500' : 'text-slate-400'}`} />
      <span>
        {isExhausted 
          ? `No ${type} left today`
          : `${remaining} of ${limit} ${type} left`
        }
      </span>
    </div>
  );
};

export const PremiumBadge = () => (
  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-yellow-400 to-amber-500 text-white text-sm font-bold rounded-full">
    <Crown className="w-4 h-4" />
    Premium
  </div>
);

export default UpgradeModal;
